import React from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import useDateFormatter from 'src/hooks/useDateFormatter';
import { toLocalDateFromInstant } from 'src/util/partialDate';
import { useAutoTranslation } from 'src/hooks/useTranslate';

// The stage 1 screener a questionnaire link was issued with, as the clinic sees
// it (TGCLOUD-1910): which instrument, the outcome, when, and every answer
// beside its question. Shown in the Family History section.
export default function ScreeningSummary({ screening }) {
  const t = useAutoTranslation();
  const fmtDate = useDateFormatter();
  if (!screening) return null;
  const { shortName, outcome, positiveCount, cutPoint, completedAt, answers } =
    screening;
  const total = answers?.length || 0;
  const when = completedAt
    ? fmtDate(toLocalDateFromInstant(completedAt))
    : null;

  let line;
  if (!outcome) {
    line = t('{{name}} screening questions: not answered yet.', {
      name: shortName
    });
  } else if (outcome === 'negative') {
    line = t(
      '{{name}}: {{count}} of {{total}} answered yes, below the cut point of {{cutPoint}}. Screened out on {{date}}.',
      {
        name: shortName,
        count: positiveCount ?? 0,
        total,
        cutPoint,
        date: when
      }
    );
  } else {
    line = t(
      '{{name}}: {{count}} of {{total}} answered yes, at or above the cut point of {{cutPoint}}. Screened in on {{date}}.',
      {
        name: shortName,
        count: positiveCount ?? 0,
        total,
        cutPoint,
        date: when
      }
    );
  }

  return (
    <Box>
      <Typography variant="body2">{line}</Typography>
      {outcome && total > 0 && (
        <Box component="ol" sx={{ m: 0, mt: 1, pl: 2.5 }}>
          {answers.map((a) => (
            <Typography
              component="li"
              variant="caption"
              key={a.key}
              sx={{ display: 'list-item', color: 'text.secondary' }}
            >
              {a.text ? t(a.text) : a.key}{' '}
              <Box
                component="span"
                sx={{ fontWeight: 600, color: 'text.primary' }}
              >
                {a.answer ? t('Yes') : t('No')}
              </Box>
            </Typography>
          ))}
        </Box>
      )}
    </Box>
  );
}
